import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import Head from 'next/head';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useDelayedRender from 'shared/hooks/use-delayed-render';
import type { Dispensaries } from 'src/gql/graphql';
import useUI from 'src/hooks/use-ui';
import { useIsAccountEnabled } from 'src/hooks/use-is-account-enabled';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import StoreFrontHeader from 'src/dispensary/core-menu/components/core-menu-header/store-front-header';
import { Navigation } from 'src/dispensary/core-menu/components/header/navigation';
import { PlaceholderHeader } from 'src/dispensary/core-menu/components/header/placeholder-header';
import { PlaceholderNavigation } from 'src/dispensary/core-menu/components/header/placeholder-navigation';
import { useMetaFields } from 'src/theme/provider/commerce-components/use-meta-fields';
import useExtractedMeta from 'src/theme/provider/commerce-components/use-extracted-meta';
import { SkipLink } from './skip-link';

type HeaderProps = {
  dispensary?: Dispensaries | null;
};

// Delay showing placeholders to avoid flash when data loads quickly
const PLACEHOLDER_DELAY_MS = 200;

export const Header = ({ dispensary }: HeaderProps): JSX.Element | null => {
  const flags = useFlags();
  const UI = useUI();
  const delayedRender = useDelayedRender(PLACEHOLDER_DELAY_MS);
  const isAccountEnabled = useIsAccountEnabled();
  const isStoreFrontOrKiosk = UI.isStoreFront || UI.isKiosk;

  const { components } = useCommerceComponents();
  const ResolvedNavigation = useRemoteComponent(RemoteComponentSlot.Navigation, { fallback: Navigation });

  const derivedMetaFieldsEnabled: boolean = flags['growth.ecomm.get-store-front-meta-fields-function.rollout'] ?? false;
  // Extract meta components early to avoid conditional hook calls
  const ResolvedMeta = useMemo(() => components.StoreFrontMeta({ fallback: () => <></> }), [components]);
  const extractedMeta = useExtractedMeta(ResolvedMeta, derivedMetaFieldsEnabled);

  // Get meta fields from the remote theme's getStoreFrontMetaFields function
  const metaFields = useMetaFields(!derivedMetaFieldsEnabled);

  const titleWithFallback = metaFields?.title?.trim() ? metaFields.title : dispensary?.name ?? '';

  if (!dispensary) {
    return delayedRender(() => (
      <Wrapper $isSticky={UI.isKiosk} data-testid='dispensary-header'>
        <SkipLink />
        {UI.isKiosk || UI.isStoreFront ? <PlaceholderHeader /> : null}
        <PlaceholderNavigation />
      </Wrapper>
    ));
  }

  return (
    <Wrapper $isSticky={UI.isKiosk} data-testid='dispensary-header'>
      <SkipLink />
      {extractedMeta.length > 0 && <Head>{extractedMeta}</Head>}
      {metaFields && (
        <Head>
          {/* Always render title tag so Next.js can update it */}
          <title key='title'>{titleWithFallback}</title>

          {metaFields.description && <meta name='description' content={metaFields.description} />}

          {metaFields.ogImage && (
            <>
              <meta property='og:image' content={metaFields.ogImage} />
              <meta name='twitter:image' content={metaFields.ogImage} />
            </>
          )}

          {metaFields.canonical && <link rel='canonical' href={metaFields.canonical} />}

          {metaFields.structuredData && (
            <script
              type='application/ld+json'
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(metaFields.structuredData),
              }}
            />
          )}

          {metaFields.customMeta?.map((meta, index) => {
            const key = meta.name ?? meta.property ?? `custom-meta-${String(index)}`;

            if (meta.name) {
              return <meta key={key} name={meta.name} content={meta.content} />;
            }

            if (meta.property) {
              return <meta key={key} property={meta.property} content={meta.content} />;
            }

            return null;
          })}
        </Head>
      )}

      {isStoreFrontOrKiosk ? (
        <>
          <StoreFrontHeader />
          <ResolvedNavigation dispensary={dispensary} isAccountEnabled={isAccountEnabled} />
        </>
      ) : (
        <Navigation dispensary={dispensary} isAccountEnabled={isAccountEnabled} />
      )}
    </Wrapper>
  );
};

const Wrapper = styled.header<{ $isSticky: boolean }>`
  width: 100%;

  ${({ $isSticky }) =>
    $isSticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 20;
    `}
`;
